/**
 * <StayWithTutorFlow> — « Garder son tuteur », de bout en bout (template).
 *
 * Un tuteur ne peut plus assurer un ou plusieurs cours d'une matière, et rien de sa semaine ne
 * tombe dans les créneaux de la famille : un nouveau tuteur les reprendra, sauf si la famille
 * réserve, avant l'échéance, des créneaux de ce tuteur — ou, quand le serveur l'offre, garde le
 * tuteur pour l'autre matière de son bloc. Une étape — « Que préférez-vous ? » —, puis l'accusé.
 *
 * Contrôlé : l'étape, le choix, l'envoi et l'issue arrivent en props, les gestes repartent en
 * callbacks. La machine vit côté écran (`useStayWithTutorFlow`) — c'est ce qui rend chaque état
 * regardable seul en story.
 *
 * Choisir d'abord, confirmer ensuite : toucher le dernier créneau qu'il faut, ou l'une des deux
 * autres voies, pose la question « Confirmer votre choix ? », qui dit ce qui va se passer ; « Modifier »
 * efface le choix. Le pied n'a pas de bouton, sauf « Confirmer mes choix » sur un choix de plusieurs
 * créneaux qui peut partir sans être complet (un tuteur souhaité). La croix ferme sans rien changer :
 * la ligne reste dans « À faire » jusqu'à l'échéance.
 *
 * Toute la copie du geste est ICI, écrite pour le parent (vouvoiement, l'enfant à la troisième
 * personne).
 */
import React from "react"

import { Button } from "../../atoms/Buttons/Button"
import { ConfirmDialog } from "../../molecules/ConfirmDialog"
import { FlowOutcome } from "../../molecules/FlowOutcome"
import { StayWithTutorStep } from "../../organisms/StayWithTutorStep"
import {
    type UITutorSlotMove,
    type UITutorSlotMoveChoice,
    type UITutorSlotMoveOption,
    type UITutorSlotMoveOutcome,
    type UITutorSlotMovePick,
    type UITutorSlotMoveSplit,
} from "../../../ports/primary/ui/types"
import { formatNumericDayMonth } from "../../../ports/primary/ui/formatters/dateFormatter"
import { capitalizeFirst } from "../../../ports/primary/ui/formatters/textFormatter"
import { bookedOutcomeOf } from "../../../ports/primary/ui/formatters/tutorSlotMoveFormatter"
import { ParentFlowModal } from "../ParentFlowModal"

export type StayWithTutorFlowStep = "choice" | "done" | "closed"

export type StayWithTutorFlowProps = {
    visible: boolean
    onClose: () => void
    step: StayWithTutorFlowStep
    childFirstname: string
    /** Le choix en attente ; `null` tant qu'aucun n'est ouvert. */
    move: UITutorSlotMove | null

    options?: UITutorSlotMoveOption[]
    keepLabel: string
    /** Combien de créneaux cocher. Défaut : un par heure du choix. */
    pick?: UITutorSlotMovePick
    /** La troisième voie, quand le serveur l'offre. Défaut : celle du choix. */
    split?: UITutorSlotMoveSplit | null
    optionsLoading?: boolean
    selected?: UITutorSlotMoveChoice | null
    onSelect: (choice: Exclude<UITutorSlotMoveChoice, { kind: "book" }>) => void
    onToggleOption: (option: UITutorSlotMoveOption) => void

    /** La question « Confirmer votre choix ? » est ouverte. */
    confirming?: boolean
    /** « Confirmer mes choix » : poser la question sur un choix de plusieurs créneaux qui peut partir. */
    onAskConfirm: () => void
    /** « Modifier » : fermer la question et effacer le choix. */
    onCancelConfirm: () => void
    onConfirm: () => void
    /** Le choix peut-il partir (assez de créneaux cochés) ? Défaut : un choix est posé. */
    canConfirm?: boolean
    confirmLoading?: boolean
    /** Ce que le serveur a enregistré — l'accusé de l'étape `done`. */
    outcome?: UITutorSlotMoveOutcome | null

    /** Un envoi refusé (la place vient d'être prise, la fenêtre est close) : l'étape reste. */
    errorMessage?: string
    testID?: string
}

/** « le cours du mardi 19h » · « les cours du mardi 17h et mardi 18h ». */
function freedCourses(move: UITutorSlotMove): string {
    return move.hours > 1 ? `les cours du ${move.freedLabel}` : `le cours du ${move.freedLabel}`
}

const NEW_TUTOR_DETAIL = "Vous serez prévenus dès qu'il est choisi."
const CLOSED_MESSAGE = "Le choix n'est plus possible : un nouveau tuteur reprend ce cours."

/** « L'autre heure passe à un nouveau tuteur. » — les heures que les créneaux choisis ne gardent pas. */
function leftHours(left: number): string {
    return `${left === 1 ? "L'autre heure passe" : `Les ${left} autres heures passent`} à un nouveau tuteur.`
}

function outcomeMessage(
    outcome: UITutorSlotMoveOutcome | null | undefined,
    move: UITutorSlotMove,
    childFirstname: string
): { title: string; message: string; detail?: string } {
    const tutorName = move.tutor.firstname || "son tuteur"
    if (outcome?.kind === "booked") {
        const from = formatNumericDayMonth(outcome.effectiveOn)
        const left = move.hours - outcome.count
        const detail = left <= 0 ? undefined : `${leftHours(left)} ${NEW_TUTOR_DETAIL}`
        return outcome.count > 1
            ? {
                  title: "Créneaux réservés",
                  message: `${childFirstname} garde ${tutorName} pour les cours du ${outcome.label}, à partir du ${from}.`,
                  detail,
              }
            : {
                  title: "Créneau réservé",
                  message: `${childFirstname} garde ${tutorName} le ${outcome.label}, à partir du ${from}.`,
                  detail,
              }
    }
    if (outcome?.kind === "split") {
        const choice = outcome.label ? outcome.label.charAt(0).toLowerCase() + outcome.label.slice(1) : ""
        return {
            title: "C'est noté",
            message: choice ? `Votre choix est enregistré : ${choice}.` : "Votre choix est enregistré.",
            detail: "Vous serez prévenus dès que le nouveau tuteur est choisi.",
        }
    }
    return {
        title: "C'est noté",
        message: `Un nouveau tuteur reprend ${freedCourses(move)}.`,
        detail: NEW_TUTOR_DETAIL,
    }
}

/**
 * Ce que la question de confirmation dit du choix, avant qu'il ne parte : la phrase de l'accusé
 * (garder le tuteur, ou un nouveau tuteur), et l'heure que les créneaux choisis ne gardent pas.
 */
function choiceSummary(
    selected: UITutorSlotMoveChoice,
    move: UITutorSlotMove,
    childFirstname: string,
    split: UITutorSlotMoveSplit | null | undefined
): string {
    if (selected.kind === "split") return `${capitalizeFirst(split?.label ?? "")}.`
    if (selected.kind === "keep") return outcomeMessage({ kind: "kept" }, move, childFirstname).message
    const { message } = outcomeMessage(bookedOutcomeOf(selected.options), move, childFirstname)
    const left = move.hours - selected.options.length
    return left > 0 ? `${message} ${leftHours(left)}` : message
}

export function StayWithTutorFlow(props: StayWithTutorFlowProps) {
    const {
        visible,
        onClose,
        step,
        childFirstname,
        move,
        options = [],
        keepLabel,
        pick,
        split,
        optionsLoading = false,
        selected,
        onSelect,
        onToggleOption,
        confirming = false,
        onAskConfirm,
        onCancelConfirm,
        onConfirm,
        canConfirm = selected != null,
        confirmLoading = false,
        outcome,
        errorMessage,
        testID,
    } = props

    if (move == null) return null

    const tutorName = move.tutor.firstname || "Le tuteur"
    const context = `${tutorName} ne peut plus assurer ${freedCourses(move)}`

    if (step === "closed") {
        // L'échéance est passée : ni phrase d'échéance, ni créneau, ni autre voie — le message seul.
        return (
            <ParentFlowModal
                visible={visible}
                onClose={onClose}
                title="Changement de tuteur"
                stepTitle="Le choix est fermé"
                step={1}
                totalSteps={1}
                context={context}
                footer={<Button label="Fermer" variant="outline" onPress={onClose} />}
                testID={testID}
            >
                <FlowOutcome iconName="clock" tone="warning" message={errorMessage ?? CLOSED_MESSAGE} />
            </ParentFlowModal>
        )
    }

    if (step === "done") {
        const { title, message, detail } = outcomeMessage(outcome, move, childFirstname)
        return (
            <ParentFlowModal
                visible={visible}
                onClose={onClose}
                title="Changement de tuteur"
                stepTitle={title}
                step={1}
                totalSteps={1}
                context={`pour ${childFirstname}`}
                footer={<Button label="Fermer" variant="outline" onPress={onClose} />}
                testID={testID}
            >
                <FlowOutcome iconName="check" tone="success" message={message} detail={detail} />
            </ParentFlowModal>
        )
    }

    const effectivePick = pick ?? { min: move.hours, max: move.hours }
    const effectiveSplit = split !== undefined ? split : move.split

    return (
        <>
            <ParentFlowModal
                visible={visible}
                onClose={onClose}
                title="Changement de tuteur"
                stepTitle="Que préférez-vous ?"
                step={1}
                totalSteps={1}
                context={context}
                errorMessage={errorMessage}
                footer={
                    canConfirm && effectivePick.max > 1 ? (
                        <Button label="Confirmer mes choix" onPress={onAskConfirm} fullWidth />
                    ) : undefined
                }
                testID={testID}
            >
                <StayWithTutorStep
                    tutor={move.tutor}
                    freedLabel={move.freedLabel}
                    hours={move.hours}
                    deadline={move.deadline}
                    options={options}
                    pick={effectivePick}
                    keepLabel={keepLabel}
                    split={effectiveSplit}
                    selected={selected}
                    onToggleOption={onToggleOption}
                    onSelect={onSelect}
                    loading={optionsLoading}
                />
            </ParentFlowModal>
            <ConfirmDialog
                visible={visible && confirming && selected != null}
                title="Confirmer votre choix ?"
                message={
                    selected != null
                        ? `${choiceSummary(selected, move, childFirstname, effectiveSplit)} Vous recevrez un e-mail de confirmation.`
                        : undefined
                }
                confirmLabel={confirmLoading ? "Confirmation…" : "Confirmer"}
                cancelLabel="Modifier"
                danger={false}
                onConfirm={onConfirm}
                onCancel={onCancelConfirm}
            />
        </>
    )
}
